import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// material
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination
} from '@mui/material';

// graphql
import { useQuery } from '@apollo/client';
import { AdminGQLFunction } from '../graphql/ADMIN';

// components
import Page from '../components/Page';
import Label from '../components/Label';
import Scrollbar from '../components/Scrollbar';
import Iconify from '../components/Iconify';
import SearchNotFound from '../sections/people/SearchNotFound';
import { ListToolbar, ListHead, ListMoreMenu, LoadingList } from '../sections/people';
// import BasicBreadcrumb from '../components/BasicBreadcrumb';
import EmptyList from '../sections/people/EmptyList';

// info
import { ADMIN } from '../info/ADMIN';

// scss
import './Table.scss';

function TableContent({ data, page, rowsPerPage }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <TableBody>
      {data.map((user, index) => (
        <TableRow hover key={index} onClick={() => navigate(`/admin/info/${user.id}`)}>
          <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
          <TableCell>
            <Typography variant="subtitle2" noWrap>
              {user.username}
            </Typography>
          </TableCell>
          <TableCell align="left">{user.email}</TableCell>
          <TableCell align="left">
            <Label variant="ghost" color={!user.userState ? 'success' : 'warning'}>
              {t(`common.${ADMIN.ADMIN_STATE[user.userState]}`)}
            </Label>
          </TableCell>
          <TableCell align="left">{user.telephone}</TableCell>
          <TableCell align="right">
            <ListMoreMenu userState={user.userState} id={user.id} admin />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );
}
function AllAdminTable({ page, rowsPerPage, setTotal }) {
  const { loading, data } = useQuery(AdminGQLFunction.GET_ALL_ADMINS, {
    variables: { page, rowsPerPage },
    fetchPolicy: 'network-only'
  });
  useEffect(() => {
    if (!loading && data) {
      setTotal(data.allAdmins.len);
    }
  }, [loading, data, setTotal]);
  if (loading) return <LoadingList TABLE_HEAD={ADMIN.TABLE_HEAD} />;
  if (!data?.allAdmins.len)
    return (
      <TableBody>
        <TableRow>
          <TableCell align="center" colSpan={ADMIN.TABLE_HEAD.length} sx={{ py: 3 }}>
            <EmptyList />
          </TableCell>
        </TableRow>
      </TableBody>
    );
  return <TableContent data={data.allAdmins.admins} page={page} rowsPerPage={rowsPerPage} />;
}
function SearchResultTable({ page, rowsPerPage, searchField, searchInput, setTotal }) {
  const { loading, data } = useQuery(AdminGQLFunction.GET_SEARCH_ADMINS, {
    variables: { page, rowsPerPage, searchField, searchStr: searchInput }
  });
  useEffect(() => {
    if (loading === false && data) {
      setTotal(data.searchAdmins.len);
    }
  }, [loading, data, setTotal]);
  if (loading) return <LoadingList TABLE_HEAD={ADMIN.TABLE_HEAD} />;
  return data.searchAdmins.len ? (
    <TableContent data={data.searchAdmins.admins} page={page} rowsPerPage={rowsPerPage} />
  ) : (
    <TableBody>
      <TableRow>
        <TableCell align="center" colSpan={ADMIN.TABLE_HEAD.length} sx={{ py: 3 }}>
          <SearchNotFound searchQuery={searchInput} />
        </TableCell>
      </TableRow>
    </TableBody>
  );
}

export default function Admin() {
  const { t } = useTranslation();
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchField, setSearchField] = useState('username');
  const [searchInput, setSearchInput] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(15);

  useEffect(() => {
    setPage(0);
  }, [searchField, searchInput]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  // const bread = [
  //  <Typography key="1" color="text.primary">
  //    Admins
  //  </Typography>,
  // ];
  return (
    <Page title={t('nav.admin')}>
      <Container maxWidth={false}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Stack direction="column">
            <Typography variant="h4" gutterBottom>
              {t('admin.title')}
            </Typography>
          </Stack>
          <Button
            variant="contained"
            component={RouterLink}
            to="/admin/newadmin"
            startIcon={<Iconify icon="eva:plus-fill" />}
          >
            {t('admin.new_admin')}
          </Button>
        </Stack>

        <Card>
          <ListToolbar
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            field={searchField}
            setSearchField={setSearchField}
            selection={ADMIN.SEARCH_FIELDS.map(field => ({ id: field, label: t(`common.${field.toLowerCase()}`) }))}
          />

          <TablePagination
            rowsPerPageOptions={[15, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t('common.rows_per_page')}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ListHead headLabel={ADMIN.TABLE_HEAD.map(head => ({ ...head, label: head.label ? t(`common.${head.label}`) : '' }))} />
                {searchInput ? (
                  <SearchResultTable
                    searchInput={searchInput}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    setTotal={setTotal}
                    searchField={searchField}
                  />
                ) : (
                  <AllAdminTable rowsPerPage={rowsPerPage} page={page} setTotal={setTotal} />
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
        </Card>
      </Container>
    </Page>
  );
}
